<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, watch } from "vue"
import { useForm } from "vee-validate"
import { useI18n } from "vue-i18n"
import * as yup from "yup"

import { useSourceInfo } from "@core/composables"
import { SENSOR_FORBIDDEN_IDTYPES } from "@core/constants"
import type { SourceOperator } from "@types"

// ================================
// TYPES
// ================================
interface Props {
    /**
     * Valeurs initiales du formulaire
     */
    value?: {
        idsource?: string
        operator?: string
        apikey?: string
    }
}

interface ManualFormValue {
    idsource: string
    operator: string
    apikey?: string
    idtype?: string
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    value: () => ({
        idsource: "",
        operator: "",
        apikey: "",
    }),
})

const emit = defineEmits<{
    "click:submit": [value: ManualFormValue]
    "click:cancel": []
}>()

// ================================
// COMPOSABLES
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            form: {
                operator_required: "L'opérateur est obligatoire.",
                idsource_required: "L'identifiant du capteur est obligatoire.",
                idsource_invalid:
                    "L'identifiant doit correspondre au format requis pour cet opérateur.",
                apikey_required: "Le nom de la clé API est obligatoire.",
                apikey_invalid:
                    "Le nom de clé API n'est pas reconnu pour cet opérateur.",
                operator_label: "Opérateur",
                idsource_label: "Identifiant du capteur",
                apikey_label: "Nom de la clé API",
                submit: "Valider",
                cancel: "Annuler",
            },
        },
    },
})

const { getOperators, getOperator } = useSourceInfo()

// ================================
// COMPUTED
// ================================

/**
 * Opérateurs jamais proposés à la saisie manuelle d'un capteur : ils collectent
 * de la donnée (météo, réseau, relevés) mais ne correspondent à aucun capteur
 * IoT que le client puisse déclarer lui-même.
 *
 * Cette liste ne suffit pas à elle seule : c'est un pense-bête par nom, qui
 * dérive dès qu'un opérateur compteur est ajouté en base (cas `sigdu`).
 * Le filtre qui fait foi est celui sur l'`idtype`, cf.
 * `SENSOR_FORBIDDEN_IDTYPES`.
 */
const EXCLUDED_OPERATOR_IDNAMES = [
    "enedis",
    "meteofrance",
    "grdf",
    "snc",
    "openmeteo",
    "rte",
    "regaz",
    "cpcu",
    "elecsavoie",
    "dalkia",
    "sigdu",
]

/**
 * Liste des opérateurs disponibles.
 *
 * Deux filtres :
 * 1. par `idtype` — un opérateur dont l'`idtype` est un type COMPTEUR est
 *    refusé par api-metier (400 sur `POST /source/sensor`), le proposer ne
 *    peut donc mener qu'à un échec ;
 * 2. par `idname` — les opérateurs sans capteur déclarable (météo, RTE, SNC).
 */
const operatorOptions = computed<
    {
        label: string
        value: string
        img?: string
    }[]
>(() => {
    return getOperators.value
        .filter(
            (op) =>
                !SENSOR_FORBIDDEN_IDTYPES.includes(op.idtype) &&
                !EXCLUDED_OPERATOR_IDNAMES.includes(op.idname)
        )
        .map((op) => ({
            label: op.name,
            value: op.idname,
            img: op.img,
        }))
})

/**
 * Opérateur sélectionné
 */
const selectedOperator = computed<SourceOperator | undefined>(() => {
    if (!modelOperator.value) return undefined
    return getOperator(modelOperator.value)
})

// ================================
// FORMULAIRE & VALIDATION
// ================================

/**
 * Schéma de validation du formulaire
 */
const schema = yup.object({
    operator: yup
        .string()
        .required(t("form.operator_required"))
        .test("operator-exists", t("form.operator_required"), (value) => {
            return (
                !!value &&
                operatorOptions.value.some((op) => op.value === value)
            )
        }),
    idsource: yup
        .string()
        .required(t("form.idsource_required"))
        .test("idsource-format", t("form.idsource_invalid"), function (value) {
            const operatorValue = this.parent.operator
            const operator = operatorValue
                ? getOperator(operatorValue)
                : undefined
            if (!value || !operator) return true
            // Vérifie si l'idsource correspond au regex de l'opérateur
            if (!operator.idsource) return true
            const operatorRegex = new RegExp(operator.idsource)
            return operatorRegex.test(value)
        }),
    apikey: yup
        .string()
        // Obligatoire UNIQUEMENT si l'opérateur déclare des clés API : sans clé
        // en base, il n'y a rien à saisir et le champ est documenté optionnel.
        .test("apikey-required", t("form.apikey_required"), function (value) {
            const operatorValue = this.parent.operator
            const operator = operatorValue
                ? getOperator(operatorValue)
                : undefined
            if (!operator?.apikeys || operator.apikeys.length === 0) return true
            return !!value
        })
        .test("apikey-valid", t("form.apikey_invalid"), function (value) {
            const operatorValue = this.parent.operator
            const operator = operatorValue
                ? getOperator(operatorValue)
                : undefined
            if (!value || !operator) return true
            // Vérifie si la clé API existe dans la liste des clés de l'opérateur
            if (!operator.apikeys || operator.apikeys.length === 0) return true
            return operator.apikeys.some((apikey) => apikey.name === value)
        }),
})

const { errors, defineField, handleSubmit, setFieldValue } = useForm<{
    operator: string
    idsource: string
    apikey: string
}>({
    validationSchema: schema,
    initialValues: {
        operator: props.value?.operator || "",
        idsource: props.value?.idsource || "",
        apikey: props.value?.apikey || "",
    },
})

const [modelOperator] = defineField("operator")
const [modelIdsource] = defineField("idsource")
const [modelApikey] = defineField("apikey")

// ================================
// WATCHERS
// ================================

/**
 * Réinitialise les champs idsource et apikey quand l'opérateur change
 */
watch(modelOperator, () => {
    if (!modelIdsource.value) {
        setFieldValue("idsource", "")
    }
    if (!modelApikey.value) {
        setFieldValue("apikey", "")
    }
})

/**
 * Synchronise les valeurs initiales avec le formulaire
 */
watch(
    () => props.value,
    (newValue) => {
        if (newValue) {
            setFieldValue("operator", newValue.operator || "")
            setFieldValue("idsource", newValue.idsource || "")
            setFieldValue("apikey", newValue.apikey || "")
        }
    },
    { deep: true, immediate: true }
)

// ================================
// HANDLERS
// ================================

/**
 * Gère la soumission du formulaire
 */
const onSubmit = handleSubmit((values) => {
    emit("click:submit", {
        idsource: values.idsource,
        operator: values.operator,
        apikey: values.apikey || undefined,
        idtype: selectedOperator.value?.idtype,
    })
})

/**
 * Gère l'annulation
 */
function onClickCancel() {
    emit("click:cancel")
}
</script>

<template>
    <form class="flex flex-col gap-6" @submit.prevent="onSubmit">
        <!-- ================================ -->
        <!-- OPÉRATEUR -->
        <!-- ================================ -->
        <fieldset class="flex flex-col gap-4 border-none p-0">
            <legend
                class="text-lg font-semibold text-gray-700 mb-2 text-center"
            >
                1. Veuillez sélectionner un opérateur
            </legend>
            <div class="flex items-start gap-4">
                <!-- Image de l'opérateur -->
                <div
                    class="w-20 h-20 flex-shrink-0 flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200"
                >
                    <img
                        v-if="selectedOperator?.img"
                        :src="selectedOperator.img"
                        :alt="selectedOperator.name"
                        class="w-16 h-16 object-contain"
                    />
                </div>
                <!-- Sélecteur -->
                <BaseInputWrapper
                    input-id="sensor-operator"
                    :label="t('form.operator_label')"
                    :error="errors.operator"
                    class="flex-1"
                >
                    <BaseSelect
                        v-model="modelOperator"
                        input-id="sensor-operator"
                        :options="operatorOptions"
                        option-label="label"
                        option-value="value"
                        :invalid="!!errors.operator"
                        :loading="operatorOptions.length === 0"
                        class="w-full"
                    >
                        <template #value="slotProps">
                            <div
                                v-if="slotProps.value"
                                class="flex items-center gap-2"
                            >
                                <!-- Logo décoratif : le nom est déjà porté par
                                     le <span> voisin. Un alt qui le répète est
                                     redondant pour un lecteur d'écran, et si
                                     l'image ne charge pas le navigateur rend ce
                                     texte alt À CÔTÉ du libellé — le nom de
                                     l'opérateur apparaît alors deux fois. -->
                                <img
                                    v-if="selectedOperator?.img"
                                    :src="selectedOperator.img"
                                    alt=""
                                    class="w-6 h-6 object-contain"
                                />
                                <span>{{ selectedOperator?.name }}</span>
                            </div>
                        </template>
                        <template #option="slotProps">
                            <div class="flex items-center gap-2">
                                <!-- Logo décoratif : cf. le slot #value. -->
                                <img
                                    v-if="slotProps.option?.img"
                                    :src="slotProps.option.img"
                                    alt=""
                                    class="w-6 h-6 object-contain"
                                />
                                <span>{{ slotProps.option.label }}</span>
                            </div>
                        </template>
                    </BaseSelect>
                </BaseInputWrapper>
            </div>
        </fieldset>

        <!-- ================================ -->
        <!-- IDENTIFIANT DU CAPTEUR -->
        <!-- ================================ -->
        <fieldset class="flex flex-col gap-4 border-none p-0">
            <legend
                class="text-lg font-semibold text-gray-700 mb-2 text-center"
            >
                2. Veuillez renseigner l'identifiant de votre capteur
            </legend>
            <BaseInputWrapper
                input-id="sensor-idsource"
                :label="t('form.idsource_label')"
                :error="errors.idsource"
            >
                <BaseInputText
                    v-model="modelIdsource"
                    input-id="sensor-idsource"
                    data-cy="sensor-idsource"
                    :invalid="!!errors.idsource"
                />
            </BaseInputWrapper>
        </fieldset>

        <!-- ================================ -->
        <!-- CLÉ API -->
        <!-- ================================ -->
        <fieldset class="flex flex-col gap-4 border-none p-0">
            <legend
                class="text-lg font-semibold text-gray-700 mb-2 text-center"
            >
                3. Veuillez renseigner le nom de la clé API
            </legend>
            <BaseInputWrapper
                input-id="sensor-apikey"
                :label="t('form.apikey_label')"
                :error="errors.apikey"
            >
                <BaseInputText
                    v-model="modelApikey"
                    input-id="sensor-apikey"
                    data-cy="sensor-apikey"
                    :invalid="!!errors.apikey"
                />
            </BaseInputWrapper>
        </fieldset>

        <!-- ================================ -->
        <!-- ACTIONS -->
        <!-- ================================ -->
        <div class="flex flex-col sm:flex-row gap-4 mt-4">
            <BaseButton
                type="submit"
                :label="t('form.submit')"
                severity="primary"
                class="flex-1"
            />
            <BaseButton
                type="button"
                :label="t('form.cancel')"
                severity="secondary"
                class="flex-1"
                @click="onClickCancel"
            />
        </div>
    </form>
</template>
